import { CommonModule, UpperCasePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  IonButton,
  IonCol,
  IonGrid,
  IonIcon,
  IonInput,
  IonItem,
  IonItemDivider,
  IonItemGroup,
  IonLabel,
  IonList,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  ModalController,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  FormBlurNotifierDirective,
  IonInputFakeComponent,
  PreventBlurPropagationDirective,
} from '@okcargo-apps/layout-ionic';
import { EMessageLevel, EMessageType, MessagesUiService, MessageUiIonicComponent } from '@okcargo-apps/messages-ui';
import {
  EStopType,
  getEmptyStopDetailedForShipper,
  ICoords,
  IStopAddress,
  IStopDetailedForShipper,
  UUID,
} from '@okcargo-apps/shared-domain';
import { IPlace } from '@okcargo-apps/shared-domain-ionic';
import { PlatformService } from '@okcargo-apps/shared-infrastructure-ionic';
import { dateSinceAfterNowValidator, dateUntilAfterSinceValidator, phoneUEValidator } from '@okcargo-apps/utils';
import { format } from 'date-fns';
import { Subscription } from 'rxjs';
import { SearchAddressbookModalComponent } from '../search-addressbook-modal/search-addressbook-modal.component';
import { SearchCityPostalcodeModalComponent } from '../search-city-postalcode-modal/search-city-postalcode-modal.component';

@Component({
  selector: 'lib-freight-shipper-edit-stop-form',
  templateUrl: './freight-shipper-edit-stop-form.component.html',
  styleUrls: ['./freight-shipper-edit-stop-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    IonItemDivider,
    IonItemGroup,
    IonList,
    IonLabel,
    IonItem,
    IonButton,
    IonIcon,
    IonGrid,
    IonRow,
    IonCol,
    IonInput,
    IonTextarea,
    IonSelect,
    IonSelectOption,
    TranslateModule,
    ReactiveFormsModule,
    FormBlurNotifierDirective,
    PreventBlurPropagationDirective,
    UpperCasePipe,
    IonInputFakeComponent,
    CommonModule,
    MessageUiIonicComponent,
  ],
})
export class FreightShipperEditStopFormComponent implements OnInit, OnDestroy {
  @Input() set stopData(value: { stop: IStopDetailedForShipper; totalNumberStops: number }) {
    this._stop.set(value.stop);
    this._totalNumberStops.set(value.totalNumberStops);

    // Only activate edit mode if we have a valid address and not comes from address book
    if (value.stop.address?.postalCode && !this.isAddressFromAddressBook()) {
      this.isAddressEditable.set(true);
      this.addAddressFieldsToForm();
    }

    this.initializeForm();
  }

  // TODO simplify this: use only one @Output.

  @Output() removeStop = new EventEmitter<UUID>();
  @Output() moveStopUp = new EventEmitter<{ stopId: UUID; newPosition: number }>();
  @Output() moveStopDown = new EventEmitter<{ stopId: UUID; newPosition: number }>();
  @Output() dataModified = new EventEmitter<UUID>();

  @ViewChild('blurNotifier') blurNotifier!: FormBlurNotifierDirective;

  private readonly fb = inject(FormBuilder);
  private readonly modalCtrl = inject(ModalController);
  private readonly messagesUiService = inject(MessagesUiService);
  private readonly platformService = inject(PlatformService);
  private readonly translate = inject(TranslateService);

  private readonly subscriptions = new Subscription();

  private readonly _totalNumberStops = signal<number>(0);
  private readonly _stop = signal<IStopDetailedForShipper>(getEmptyStopDetailedForShipper());

  public readonly isAddressEditable = signal<boolean>(false);

  public readonly stopsLength = this._totalNumberStops.asReadonly();
  public readonly currentStop = this._stop.asReadonly();

  public readonly currentStopType = computed(() => this._stop().type);
  public readonly isIntermediateStop = computed(
    () => this._stop().position !== 0 && this._stop().position !== this._totalNumberStops() - 1,
  );
  public readonly isFirstStop = computed(() => this._stop().position === 0);
  public readonly isLastStop = computed(() => this._stop().position === this._totalNumberStops() - 1);

  public isValidated = false;
  public EMessageLevel = EMessageLevel;

  // TODO remove in the future, for now only for the template
  public readonly place = computed(() => {
    const address = this._stop().address;
    return {
      city: address.city,
      postalCode: address.postalCode,
      stateCode: address.stateCode,
      countryCode: address.countryCode || 'ES', // quickfix, only for old address without countryCode
      location: address.coords,
      // country: address.country,
      // state: address.state,
    } as IPlace;
  });

  public readonly isAddressFromAddressBook = computed(() => (this._stop().address.addressbookId ? true : false));

  public readonly messages = computed(() => this.messagesUiService.getMessagesForObjectId(this._stop().id));

  public readonly minSinceDate = signal<string>(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
  public readonly minUntilDate = signal<string>(format(new Date(), "yyyy-MM-dd'T'HH:mm"));

  public readonly EStopType = EStopType;
  public readonly stopTypes = Object.values(EStopType);

  public stopForm!: FormGroup;

  public errorText: Record<string, string | null> = {};

  /* constructor & lifecycle */

  constructor() {
    this.stopForm = this.createForm();

    effect(() => {
      // Necessary for corner-case: update the ion-select value when new stop is created,
      // then the 2nd stop is moved to 1 and then same stop is moved to 2 again.
      const control = this.stopForm.get('stopType');
      const stopType = this.currentStopType();
      if (control && control.value !== stopType) {
        control.setValue(stopType, { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.setupDateControlsListeners();
    this.setupErrorText();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /** public */

  public onStopTypeChange(event: any): void {
    const stopType = event.detail.value as EStopType;
    this._stop.update((currentStop) => ({
      ...currentStop,
      type: stopType,
    }));

    this.updateValidators();
  }

  public async openModalNewAddress(): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: SearchCityPostalcodeModalComponent,
      componentProps: {},
      backdropDismiss: false,
    });

    await modal.present();
    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm' && data) {
      const newAddress: Partial<IStopAddress> = {
        ...this._stop().address,
        city: data.city,
        postalCode: data.postalCode,
        // state: data.state || '',
        stateCode: data.stateCode,
        // country: data.country || '',
        countryCode: data.countryCode,
        coords: data.location,
      };

      this._stop.update((stop) => ({
        ...stop,
        address: newAddress as IStopAddress,
      }));
      this.isAddressEditable.set(true);
      this.addAddressFieldsToForm();

      this.messagesUiService.removeMessageByCriteria({
        objectId: this._stop().id,
        type: EMessageType.STOP_WITHOUT_ADDRESS,
      });

      this.emitModifiedData();
    }
  }

  public async openModalAddressBook(): Promise<void> {
    this.blurNotifier.cancelNextBlur();

    const modal = await this.modalCtrl.create({
      component: SearchAddressbookModalComponent,
      componentProps: {},
      backdropDismiss: false,
    });

    await modal.present();
    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm' && data) {
      console.log(data);
      this._stop.update((stop) => ({
        ...stop,
        address: data as IStopAddress,
      }));

      this.isAddressEditable.set(true);
      this.addAddressFieldsToForm();

      this.messagesUiService.removeMessageByCriteria({
        objectId: this._stop().id,
        type: EMessageType.STOP_WITHOUT_ADDRESS,
      });

      this.emitModifiedData();
    }
  }

  public modifyAddress(): void {
    this._stop.update((stop) => {
      const updatedAddress = { ...stop.address };
      delete updatedAddress.addressbookId;
      return {
        ...stop,
        address: updatedAddress,
      };
    });
  }

  // TODO extract for reuse, it's used in some components
  public openMap(coords: ICoords | undefined): void {
    if (coords) {
      const lat = coords.latitude;
      const lng = coords.longitude;

      let url = `https://www.google.com/maps?q=${lat},${lng}`;
      if (this.platformService.isAndroid) {
        url = `geo:${lat},${lng}?q=${lat},${lng}`;
      } else if (this.platformService.isIos) {
        url = `maps://?q=${lat},${lng}`;
      }

      window.open(url, '_blank');
    }
  }

  public validateForm(): boolean {
    this.isValidated = true;

    this.updateAddressFromForm();

    // Note: address validation (exist and whit postal code) in src/app/freight-ship/domain/freight-ship-validation.ts

    if (this.stopForm.valid) {
      return true;
    }

    this.stopForm.markAllAsTouched();
    Object.values(this.stopForm.controls).forEach((control) => {
      control.markAsDirty();
    });

    return false;
  }

  public get formData(): IStopDetailedForShipper {
    this.updateAddressFromForm();

    const currentStop = this._stop();
    const formValues = this.stopForm.value;

    return {
      ...(currentStop || {}),
      type: this.currentStopType(),
      netWeight: formValues.weight,
      packages: formValues.packages,
      goodsDescription: formValues.nature.trim(),
      reference: formValues.reference.trim(),
      unloadedNetWeight: formValues.unloadedWeight,
      unloadedPackages: formValues.unloadedPackages,
      // reservations: formValues.reservations,
      instructions: formValues.instructions.trim(),
      since: formValues.since ? new Date(formValues.since) : undefined,
      until: formValues.until ? new Date(formValues.until) : undefined,
      address: this._stop().address,
    } as IStopDetailedForShipper;
  }

  public emitRemoveStop(): void {
    this.blurNotifier.cancelNextBlur();
    const currentStop = this._stop();
    if (currentStop) {
      this.removeStop.emit(currentStop.id);
    }
  }

  public emitMoveUp(): void {
    this.blurNotifier.cancelNextBlur();
    const currentStop = this._stop();
    if (currentStop && currentStop.position > 0) {
      const newPosition = currentStop.position - 1;
      this.moveStopUp.emit({
        stopId: currentStop.id,
        newPosition: newPosition,
      });
    }
  }

  public emitMoveDown(): void {
    this.blurNotifier.cancelNextBlur();
    const currentStop = this._stop();
    if (currentStop && currentStop.position < this._totalNumberStops()) {
      const newPosition = currentStop.position + 1;
      this.moveStopDown.emit({
        stopId: currentStop.id,
        newPosition: newPosition,
      });
    }
  }

  public emitModifiedData(): void {
    // console.log('emitModifiedData()');
    const currentStop = this._stop();
    this.dataModified.emit(currentStop.id);
  }

  // -- private

  private createForm(): FormGroup {
    return this.fb.group({
      weight: [''],
      packages: [''],
      nature: [''],
      reference: [''],
      unloadedWeight: [''],
      unloadedPackages: [''],
      // reservations: [''],
      instructions: [''],
      since: ['', [dateSinceAfterNowValidator()]],
      until: ['', [Validators.required]],
    });
  }

  private initializeForm(): void {
    if (!this._stop() || !this._totalNumberStops()) return;

    const stop = this._stop();

    if (this.isIntermediateStop()) {
      this.stopForm.addControl('stopType', this.fb.control(stop.type || '', Validators.required));
    }

    const patchData: any = {
      weight: stop.netWeight || '',
      packages: stop.packages || '',
      nature: stop.goodsDescription || '',
      reference: stop.reference || '',
      unloadedWeight: stop.unloadedNetWeight || '',
      unloadedPackages: stop.unloadedPackages || '',
      // reservations: stop.reservations || '',
      instructions: stop.instructions || '',
      since: stop.since ? format(stop.since, "yyyy-MM-dd'T'HH:mm") : '',
      until: stop.until ? format(stop.until, "yyyy-MM-dd'T'HH:mm") : '',
    };

    this.stopForm.patchValue(patchData);

    this.updateValidators();
  }

  private setupDateControlsListeners(): void {
    const untilControl = this.stopForm.get('until');
    const sinceControl = this.stopForm.get('since');

    if (untilControl && sinceControl) {
      untilControl.setValidators([Validators.required, dateUntilAfterSinceValidator(() => sinceControl.value)]);

      const sinceSubscription = sinceControl.valueChanges.subscribe((sinceValue) => {
        if (sinceValue) {
          this.minUntilDate.set(sinceValue);
          untilControl.updateValueAndValidity();
        }
      });

      this.subscriptions.add(sinceSubscription);
    }
  }

  private updateValidators(): void {
    // clean
    this.stopForm.get('weight')?.clearValidators();
    this.stopForm.get('packages')?.clearValidators();
    this.stopForm.get('nature')?.clearValidators();
    this.stopForm.get('unloadedWeight')?.clearValidators();
    this.stopForm.get('unloadedPackages')?.clearValidators();

    // add
    if (this.currentStopType() === EStopType.LOAD || this.currentStopType() === EStopType.UNLOAD_LOAD) {
      this.stopForm.get('weight')?.setValidators([Validators.required]);
      this.stopForm.get('packages')?.setValidators([Validators.required]);
      this.stopForm.get('nature')?.setValidators([Validators.required, Validators.minLength(3)]);

      this.stopForm.get('weight')?.updateValueAndValidity();
      this.stopForm.get('packages')?.updateValueAndValidity();
      this.stopForm.get('nature')?.updateValueAndValidity();
    }

    if (
      (this.currentStopType() === EStopType.UNLOAD && !this.isLastStop()) ||
      this.currentStopType() === EStopType.UNLOAD_LOAD
    ) {
      this.stopForm.get('unloadedWeight')?.setValidators([Validators.required]);
      this.stopForm.get('unloadedPackages')?.setValidators([Validators.required]);

      this.stopForm.get('unloadedWeight')?.updateValueAndValidity();
      this.stopForm.get('unloadedPackages')?.updateValueAndValidity();
    }
  }

  private setupErrorText(): void {
    // Always-present controls (not added dynamically)
    // For address form controls, see addAddressFieldsToForm()
    const controlNames = ['since', 'until', 'nature'];

    controlNames.forEach((controlName) => {
      const control = this.stopForm.get(controlName);
      control?.statusChanges.subscribe(() => {
        if (control?.invalid && (control.dirty || control.touched)) {
          this.errorText[controlName] = this.setErrorTextForControl(controlName);
        }
      });
    });
  }

  private setErrorTextForControl(controlName: string): string | null {
    const control = this.stopForm.get(controlName);
    if (!control) {
      return null;
    }

    const errorMessages: Record<string, string> = {
      required: 'Error. Es obligatorio rellenar este campo (está vacío).',
      minlength: 'Error. Es obligatorio rellenar este campo (texto insuficiente).',
    };

    const errors = control.errors;
    if (!errors) {
      return null;
    }

    for (const errorKey of Object.keys(errors)) {
      const errorValue = errors[errorKey];
      // error has message
      if (errorValue && typeof errorValue === 'object' && 'message' in errorValue) {
        return this.translate.instant(errorValue.message);
      }

      // error hasn`t message, then search in errorMessages
      if (errorKey in errorMessages) {
        return errorMessages[errorKey];
      }
    }

    return null;
  }

  private addAddressFieldsToForm(): void {
    if (!this.stopForm.contains('companyName')) {
      this.stopForm.addControl(
        'companyName',
        this.fb.control(this._stop().address.company || '', [Validators.required, Validators.minLength(3)]),
      );
      this.stopForm.addControl(
        'streetAddress',
        this.fb.control(this._stop().address.street || '', [Validators.required, Validators.minLength(3)]),
      );
      this.stopForm.addControl('streetAddress2', this.fb.control(this._stop().address.extraLine || ''));
      this.stopForm.addControl(
        'companyPhone',
        this.fb.control(this._stop().address.phone || '', [phoneUEValidator('ES', false)]),
      );
      this.stopForm.addControl('companyTaxNumber', this.fb.control(this._stop().address.taxId || ''));

      // Subscription to get validation error messages
      const companyPhoneControl = this.stopForm.get('companyPhone');
      if (companyPhoneControl) {
        const subscription = companyPhoneControl.statusChanges.subscribe(() => {
          if (companyPhoneControl.invalid && (companyPhoneControl.dirty || companyPhoneControl.touched)) {
            this.errorText['companyPhone'] = this.setErrorTextForControl('companyPhone');
          } else {
            this.errorText['companyPhone'] = null;
          }
        });
        this.subscriptions.add(subscription);
      }
    } else {
      this.stopForm.patchValue({
        companyName: this._stop().address.company || '',
        streetAddress: this._stop().address.street || '',
        streetAddress2: this._stop().address.extraLine || '',
        companyPhone: this._stop().address.phone || '',
        companyTaxNumber: this._stop().address.taxId || '',
      });
    }
  }

  private updateAddressFromForm(): void {
    if (!this.stopForm.contains('companyName')) return;

    this._stop.update((stop) => ({
      ...stop,
      address: {
        ...stop.address,
        company: this.stopForm.get('companyName')?.value.trim() || '',
        street: this.stopForm.get('streetAddress')?.value.trim() || '',
        extraLine: this.stopForm.get('streetAddress2')?.value.trim() || '',
        phone: this.stopForm.get('companyPhone')?.value.trim() || '',
        taxId: this.stopForm.get('companyTaxNumber')?.value.trim() || '',
      },
    }));
  }
}
